import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button,
  CircularProgress, Typography, TextField, Box, Container, Pagination, Card, CardContent,
  Avatar, FormGroup, FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, InputAdornment, IconButton, Menu
} from '@mui/material';
import {
  SaveAlt as SaveAltIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { API_BASE_URL } from '../constants/APIConstants';
import * as XLSX from 'xlsx';
import { auth } from '../firebase';
import { format, parseISO } from 'date-fns';
import AccountDetails from './AccountDetails'; // Import the modified AccountDetails component
import './adminDashboard.css';

// Helper functions (formatDate, convertToTitleCase) remain the same:
export const formatDate = (isoDate) => {
  if (!isoDate) return 'N/A';
  try {
    const cleanDate = isoDate.split('[')[0];
    const parsedDate = parseISO(cleanDate);
    if (isNaN(parsedDate)) {
      throw new Error('Invalid date');
    }
    return format(parsedDate, 'MMM dd, yyyy h:mm a') + ' EST';
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};


const convertToTitleCase = (camelCaseString) => {
  if (!camelCaseString || typeof camelCaseString !== 'string') {
    return '';
  }
  return camelCaseString
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};


const WaitlistDashboard = () => {
  const [currentTab, setCurrentTab] = useState('pending');
  const [tableData, setTableData] = useState([]);
  const [tabCounts, setTabCounts] = useState({ pending: 0, approved: 0, rejected: 0, notRegistered: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const itemsPerPage = 10;

  // State for filter checkboxes
  const [filters, setFilters] = useState({
    portfolioImages: null,
    isEmailVerified: null,
    hasDescription: null,
    socialMediaLinked: null
  });

  // New state to handle the account modal
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);

  useEffect(() => {
    const loadCountsFromLocalStorage = () => {
      const pendingCount = localStorage.getItem('pendingCount');
      const approvedCount = localStorage.getItem('approvedCount');
      const rejectedCount = localStorage.getItem('rejectedCount');
      const notRegisteredCount = localStorage.getItem('notRegisteredCount');
      setTabCounts({
        pending: pendingCount ? parseInt(pendingCount) : 0,
        approved: approvedCount ? parseInt(approvedCount) : 0,
        rejected: rejectedCount ? parseInt(rejectedCount) : 0,
        notRegistered: notRegisteredCount ? parseInt(notRegisteredCount) : 0,
      });
    };
    loadCountsFromLocalStorage();
    fetchData(currentTab);
  }, [currentTab]);

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value === prev[filterName] ? null : value
    }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      portfolioImages: null,
      isEmailVerified: null,
      hasDescription: null,
      socialMediaLinked: null
    });
    setCurrentPage(1);
  };

  const closeFilterMenu = () => {
    setFilterAnchorEl(null);
  };

  // Instead of navigating, open the modal with the selected account data
  const handleViewProfile = (accountId) => {
    const account = tableData.find((account) => account.accountId === accountId);
    if (account) {
      setSelectedAccount(account);
      setIsAccountModalOpen(true);
    }
  };

  // Handler to approve or reject waitlist entries
  const handleStatusUpdate = async (accountId, status) => {
    setLoading(true);
    try {
      const token = await auth.currentUser.getIdToken();
      // Update waitlist status
      await axios.put(
        `${API_BASE_URL}/api/admin/updateWaitlistStatus`,
        null,
        {
          params: { accountId, status },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // Update verification status as well
      await axios.put(
        `${API_BASE_URL}/api/admin/updateVerificationStatus`,
        null,
        {
          params: { accountId, status },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // Refresh the data for the current tab
      fetchData(currentTab);
    } catch (error) {
      console.error('Error updating waitlist status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (status) => {
    setLoading(true);
    let apiUrl;
    if (status === 'notRegistered') {
      apiUrl = `${API_BASE_URL}/api/admin/getAllAccountDoesNotHaveEmail`;
    } else {
      apiUrl = `${API_BASE_URL}/api/admin/getUnVerifiedAccounts?status=${status}`;
    }
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await axios.get(apiUrl, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data.length === 0) {
        setTableData([]);
        setTabCounts((prevCounts) => {
          localStorage.setItem(`${status}Count`, 0);
          return { ...prevCounts, [status]: 0 };
        });
        return;
      }

      const transformedData = response.data.map((item) => {
        if (status === 'notRegistered') {
          return {
            phoneNumber: item.phoneNumber || 'N/A',
            createdAt: formatDate(new Date(item.createdAt).toISOString()),
          };
        } else {
          return {
            accountId: item.accountDetails?.accountId || 'N/A',
            name: item.accountDetails?.accountName || 'N/A',
            email: item.accountDetails?.accountEmail || 'N/A',
            contact: item.accountDetails?.accountPhone || 'N/A',
            portfolioImages: item.portfolioCollections?.some(
              (collection) => Array.isArray(collection.portfolioDocuments) && collection.portfolioDocuments.length > 0
            )
              ? 'Yes'
              : 'No',
            isEmailVerified: item.emailVerified,
            accountDescription: item.accountDetails?.accountDescription || 'N/A',
            socialMediaLinked: item.accountDetails?.socialMediaLinked,
            socialLinks: item.accountDetails?.socialLinks || item.accountDetails?.socials || item.accountDetails?.socialMediaLinks || {},
            accountCreatedAt: formatDate(item.accountDetails?.accountCreatedAt),
            accountProfileURL: item.accountDetails?.accountProfileURL || 'No',
            accountCategory: convertToTitleCase(item.accountDetails?.accountCategory) || 'No',
            accountSubCategory: convertToTitleCase(item.accountDetails?.accountSubCategory) || 'No',
            Profile: item.accountDetails?.accountProfileURL || 'N/A', 
            status: item.status || 'pending', // Assuming a status field exists
            accountRating: item.accountDetails?.accountRating || '0', // Assuming a rating field exists
            accountTotalRatings: item.accountDetails?.accountTotalRatings || '0', // Assuming a total rating field exists
            portfolioCollections: item.portfolioCollections || [],
          };
        }
      });
      setTableData(transformedData);
      setTabCounts((prevCounts) => {
        const updatedCounts = { ...prevCounts, [status]: transformedData.length };
        localStorage.setItem(`${status}Count`, transformedData.length);
        return updatedCounts;
      });
    } catch (error) {
      const status = error?.response?.status;
      const body = error?.response?.data;
      console.error('Error fetching data:', { url: apiUrl, status, body, error });
      if (status === 401 || status === 403) {
        alert(`Access error ${status}. Make sure you are logged into the correct Firebase project and have admin rights in PROD.`);
      }
    } finally {
      setLoading(false);
    }
  };
  const filteredData = tableData.filter((row) => {
    const searchTerm = searchText.toLowerCase();
    if (currentTab === 'notRegistered') {
      return (
        (row.phoneNumber && row.phoneNumber.toLowerCase().includes(searchTerm))
      );
    } else {
      const matchesSearch = (
        (row.name && row.name.toLowerCase().includes(searchTerm)) ||
        (row.email && row.email.toLowerCase().includes(searchTerm)) ||
        (row.contact && row.contact.toLowerCase().includes(searchTerm)) ||
        (row.accountCategory && row.accountCategory.toLowerCase().includes(searchTerm)) ||
        (row.accountSubCategory && row.accountSubCategory.toLowerCase().includes(searchTerm))
      );
      const matchesFilters = (
        (filters.portfolioImages === null || row.portfolioImages === (filters.portfolioImages ? 'Yes' : 'No')) &&
        (filters.isEmailVerified === null || row.isEmailVerified === filters.isEmailVerified) &&
        (filters.hasDescription === null || (row.accountDescription !== 'N/A') === filters.hasDescription) &&
        (filters.socialMediaLinked === null || row.socialMediaLinked === filters.socialMediaLinked)
      );
      return matchesSearch && matchesFilters;
    }
  });

  const exportToExcel = () => {
    // Export what's currently visible (search + filters applied), not the
    // full unfiltered tab dataset - otherwise the file silently includes
    // rows the admin had filtered out on screen.
    const rowsToExport = filteredData.map(({ accountId, accountProfileURL, ...rest }) => rest);
    const headers = Object.keys(rowsToExport[0] || {}).map(convertToTitleCase);
    const dataWithHeaders = [headers, ...rowsToExport.map(Object.values)];
    const worksheet = XLSX.utils.aoa_to_sheet(dataWithHeaders);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Waitlist');
    XLSX.writeFile(workbook, 'WaitlistData.xlsx');
  };

  const handleRefresh = () => {
    fetchData(currentTab);
  };

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const currentPageData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const statusMapping = {
    notRegistered: 'Not Registered',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected'
  };

  const statusDescriptions = {
    notRegistered: 'Phone-only signups',
    pending: 'Awaiting review',
    approved: 'Ready to use',
    rejected: 'Declined accounts'
  };

  const activeFilterCount = Object.values(filters).filter((value) => value !== null).length;
  const isFilterMenuOpen = Boolean(filterAnchorEl);

  const tableHeaders = currentTab === 'notRegistered' ? [
    'Created At',
    'Phone Number'
  ] : [
    'Created At',
    'Account',
    'Category',
    'Verification',
    'Rating',
    'Actions',
  ];

  return (
    <Container maxWidth={false} className="dashboard-page">
      <Box className="dashboard-header">
        <Box>
          <div className="dashboard-eyebrow">Account Verification</div>
          <h1 className="dashboard-title">Waitlist Dashboard</h1>
          <div className="dashboard-subtitle">
            Review account readiness, registration gaps, and verification status.
          </div>
        </Box>
        <Box className="dashboard-actions">
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<SaveAltIcon />} onClick={exportToExcel}>
            Export
          </Button>
        </Box>
      </Box>

      <Box className="dashboard-metrics">
        {Object.keys(statusMapping).map((status) => (
          <Card
            key={status}
            className={`metric-card metric-card--${status} ${currentTab === status ? 'metric-card--active' : ''}`}
            onClick={() => setCurrentTab(status)}
          >
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <div className="metric-card__label">{statusMapping[status]}</div>
              <div className="metric-card__value">{tabCounts[status] || 0}</div>
              <div className="table-muted-text">{statusDescriptions[status]}</div>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Box className="dashboard-toolbar">
        <TextField
          className="dashboard-search"
          variant="outlined"
          placeholder="Search name, email, contact, category, or sub category"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchText('')} aria-label="Clear search">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Chip label={`${filteredData.length} visible`} variant="outlined" />
        {currentTab !== 'notRegistered' && (
          <>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={(event) => setFilterAnchorEl(event.currentTarget)}
            >
              Filters
              {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </Button>
            <Menu
              anchorEl={filterAnchorEl}
              open={isFilterMenuOpen}
              onClose={closeFilterMenu}
              PaperProps={{ className: 'filter-menu' }}
            >
              <Box className="filter-menu__content">
                <Box className="filter-panel__header">
                  <div className="filter-panel__title">Filters</div>
                  <Chip size="small" label={`${activeFilterCount} active`} color={activeFilterCount ? 'primary' : 'default'} />
                </Box>
                <FormGroup className="filter-panel__controls">
                  <FormControlLabel
                    control={<Checkbox checked={filters.portfolioImages === true} onChange={(e) => handleFilterChange('portfolioImages', e.target.checked)} />}
                    label="Has portfolio"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={filters.isEmailVerified === true} onChange={(e) => handleFilterChange('isEmailVerified', e.target.checked)} />}
                    label="Email verified"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={filters.hasDescription === true} onChange={(e) => handleFilterChange('hasDescription', e.target.checked)} />}
                    label="Has description"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={filters.socialMediaLinked === true} onChange={(e) => handleFilterChange('socialMediaLinked', e.target.checked)} />}
                    label="Social linked"
                  />
                </FormGroup>
                <Box className="filter-menu__footer">
                  <Button size="small" onClick={resetFilters} disabled={!activeFilterCount}>
                    Reset filters
                  </Button>
                  <Button size="small" variant="contained" onClick={closeFilterMenu}>
                    Done
                  </Button>
                </Box>
              </Box>
            </Menu>
          </>
        )}
      </Box>

      <TableContainer component={Paper} className="admin-table-card">
        <Table size="small" stickyHeader className="admin-table">
          <TableHead>
            <TableRow>
              {tableHeaders.map((header, index) => (
                <TableCell key={index}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={tableHeaders.length} align="center" className="table-empty-state">
                  <CircularProgress />
                  <Typography variant="body2" mt={1}>Loading accounts...</Typography>
                </TableCell>
              </TableRow>
            ) : currentPageData.length > 0 ? (
              currentPageData.map((row) => (
                <TableRow
                  key={row.accountId || row.phoneNumber}
                  hover
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleViewProfile(row.accountId)}
                >
                  {currentTab === 'notRegistered' ? (
                    <>
                      <TableCell>{row.createdAt}</TableCell>
                      <TableCell><span className="table-primary-text">{row.phoneNumber}</span></TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{row.accountCreatedAt}</TableCell>
                      <TableCell>
                        <Box className="table-account-cell">
                          <Avatar
                            src={row.accountProfileURL}
                            alt="Profile"
                            sx={{ width: 38, height: 38 }}
                          />
                          <Box>
                            <div className="table-primary-text">{row.name}</div>
                            <div className="table-muted-text">{row.email}</div>
                            <div className="table-muted-text">{row.contact}</div>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <div className="table-primary-text">{row.accountCategory}</div>
                        <div className="table-muted-text">{row.accountSubCategory}</div>
                      </TableCell>
                      <TableCell>
                        <Box className="table-chip-group">
                          <Chip size="small" label={`Portfolio: ${row.portfolioImages}`} color={row.portfolioImages === 'Yes' ? 'success' : 'default'} variant="outlined" />
                          <Chip size="small" label={`Email: ${row.email && row.email !== 'N/A' ? 'Yes' : 'No'}`} color={row.email && row.email !== 'N/A' ? 'success' : 'default'} variant="outlined" />
                          <Chip size="small" label={`Social: ${row.socialMediaLinked ? 'Yes' : 'No'}`} color={row.socialMediaLinked ? 'success' : 'default'} variant="outlined" />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <div className="table-primary-text">{row.accountRating}</div>
                        <div className="table-muted-text">{row.accountTotalRatings} total</div>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box className="table-actions">
                        {currentTab === 'pending' && (
                          <>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              onClick={() => handleStatusUpdate(row.accountId, 'approved')}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() => handleStatusUpdate(row.accountId, 'rejected')}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewProfile(row.accountId)}
                        >
                          View
                        </Button>
                        </Box>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableHeaders.length} align="center" className="table-empty-state">
                  <Typography variant="subtitle1" fontWeight={800}>No records found</Typography>
                  <Typography variant="body2">Try a different status tab, search term, or filter combination.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className="dashboard-pagination">
        <Typography variant="body2" color="text.secondary">
          Showing {currentPageData.length} of {filteredData.length} records
        </Typography>
        <Pagination count={totalPages} page={currentPage} onChange={(_, page) => setCurrentPage(page)} color="primary" />
      </Box>

      {/* Dialog for Account Details */}
      <Dialog
        open={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ className: 'profile-dialog' }}
      >
        <DialogTitle className="profile-dialog__title">
          <Box>
            <Typography className="profile-dialog__eyebrow">Admin Review</Typography>
            <Typography className="profile-dialog__heading">Account Profile</Typography>
          </Box>
          <Button onClick={() => setIsAccountModalOpen(false)} className="profile-dialog__close">
            Close
          </Button>
        </DialogTitle>
        <DialogContent className="profile-dialog__content">
          {selectedAccount ? (
            <AccountDetails
              account={selectedAccount}
              onClose={() => setIsAccountModalOpen(false)}
              onStatusChange={() => fetchData(currentTab)}
            />
          ) : (
            <Typography>No account data available.</Typography>
          )}
        </DialogContent>
        <DialogActions className="profile-dialog__footer">
          <Typography variant="caption" color="text.secondary">
            Review profile details before changing account status.
          </Typography>
          <Button onClick={() => setIsAccountModalOpen(false)} color="primary" variant="outlined">Done</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WaitlistDashboard;
